import { z } from 'zod';
import { dbPortalDokumentArt, dbPortalKomponente, dbPortalKomponentenArt } from './db-portal-lieferung.js';
// ---------------------------------------------------------------------------
// DB-Portal — Verträge der statischen Datendateien (Export aus
// aufschaltgebiete.db über tools/export-tab-db.py). Jede Datei trägt ein
// `schema`-Kennzeichen; eine inkompatible Änderung erhöht die Versionsnummer.
// ---------------------------------------------------------------------------
const isoDatum = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const DB_PORTAL_SKALA_STATUS = ['bewertet', 'vorläufig', 'offen'];
export const dbPortalSkalaStatus = z.enum(DB_PORTAL_SKALA_STATUS);
export const DB_PORTAL_MERKMAL_STATUS = ['gleich', 'abweichend', 'offen', 'entfällt'];
export const dbPortalMerkmalStatus = z.enum(DB_PORTAL_MERKMAL_STATUS);
/** Eintrag der Zeitreihe je Gebiet (kompakt, für Karte und Zeitstrahl). */
export const dbPortalZeitreiheEintrag = z.object({
    f: z.number().int(),
    von: isoDatum,
    bis: isoDatum.nullable(),
    k: z.array(z.string()),
    u: z.boolean(),
});
export const dbPortalGebietProps = z.object({
    id: z.number().int(),
    slug: z.string().min(1),
    name: z.string().min(1),
    typ: z.string().nullable(),
    bundesland: z.string().nullable(),
    fassungen: z.number().int().nonnegative(),
    erste: isoDatum.nullable(),
    letzte: isoDatum.nullable(),
    zeitreihe: z.array(dbPortalZeitreiheEintrag),
});
export const dbPortalGebietFeature = z.object({
    type: z.literal('Feature'),
    id: z.number().int(),
    geometry: z.object({
        type: z.literal('Point'),
        coordinates: z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]),
    }),
    properties: dbPortalGebietProps,
});
export const dbPortalGebiete = z.object({
    type: z.literal('FeatureCollection'),
    features: z.array(dbPortalGebietFeature),
});
export const dbPortalFassung = z.object({
    id: z.number().int(),
    stand: z.string().nullable(),
    stand_datum: isoDatum.nullable(),
    von: isoDatum.nullable(),
    von_art: z.enum(['stand', 'erstnachweis']).nullable(),
    bis: isoDatum.nullable(),
    quelle_art: z.enum(['wayback', 'web', 'manuell', 'unbekannt']),
    quelle_url: z.string().url().nullable(),
    pdf_status: z.enum(['ok', 'fehlt', 'gekappt', 'unbekannt']),
    seiten: z.number().int().nullable(),
    ausgewertet: z.boolean(),
    modell: z.string().nullable(),
    modell_fein: z.string().nullable(),
    konzessionaere: z.array(z.string()),
    konzessionaer_herkunft: z.enum(['regex', 'llm', 'regex+llm', 'keine', 'uebernommen']),
    uebernommen_aus: z.number().int().nullable(),
    drittanbieter: z.string().nullable(),
    preise_vorhanden: z.boolean().nullable(),
    beleg: z.string().nullable(),
    stand_hinweis: z.string().nullable(),
});
export const dbPortalTabFassungen = z.object({
    schema: z.literal('db-portal/tab-fassungen/1'),
    gebiete: z.record(z.string().regex(/^\d+$/), z.array(dbPortalFassung)),
});
export const dbPortalKonzessionaere = z.object({
    schema: z.literal('db-portal/konzessionaere/1'),
    firmen: z.array(z.object({
        id: z.string().min(1),
        name: z.string().min(1),
        gebiete: z.number().int().nonnegative(),
        fassungen: z.number().int().nonnegative(),
    })),
});
/**
 * Vergleichbarkeitsskala je Gebiet (Schnittstelle zur Sitzung „TAB-Abgleich
 * weitere Standorte“). Wert 0–5; bei Status „offen“ immer null.
 */
export const dbPortalSkalaEintrag = z
    .object({
    wert: z.number().int().min(0).max(5).nullable(),
    wert_herkunft: z.enum(['angegeben', 'berechnet']).nullable(),
    status: dbPortalSkalaStatus,
    referenz: z.boolean(),
    begruendung: z.string().nullable(),
    quelle: z.string().nullable(),
    merkmale: z.record(z.string(), dbPortalMerkmalStatus).nullable(),
    aktualisiert_am: isoDatum.nullable(),
})
    .refine((e) => (e.status === 'offen') === (e.wert === null), {
    message: 'Status „offen“ genau dann, wenn kein Wert vorliegt',
});
export const dbPortalSkala = z.object({
    schema: z.literal('db-portal/skala/1'),
    stand: isoDatum,
    beschreibung: z.string().nullable(),
    merkmale: z.array(z.object({ id: z.string().min(1), name: z.string().min(1) })),
    gebiete: z.record(z.string(), dbPortalSkalaEintrag),
});
export const dbPortalMeta = z.object({
    schema: z.literal('db-portal/meta/1'),
    exportiert_am: z.string(),
    skript_version: z.number().int(),
    quelle: z.object({ datei: z.string(), sha256: z.string().length(64), geaendert_am: z.string() }),
    anzahl: z.record(z.string(), z.number().int()),
    hinweise: z.array(z.string()),
});
// ---------------------------------------------------------------------------
// DB-Portal — API-Verträge (Phase B: Anmeldung, Vertraulichkeit, Standorte)
// ---------------------------------------------------------------------------
export const DB_PORTAL_ROLLEN = ['db_user', 'gql_admin'];
export const dbPortalRolle = z.enum(DB_PORTAL_ROLLEN);
export const dbPortalAnmeldungAnfordern = z.object({
    email: z.string().trim().toLowerCase().email().max(254),
});
export const dbPortalAnmeldungEinloesen = z.object({
    token: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
});
export const dbPortalIch = z.object({
    angemeldet: z.boolean(),
    nutzer: z
        .object({
        id: z.string().uuid(),
        anzeigename: z.string().nullable(),
        rolle: dbPortalRolle,
        ndaErforderlich: z.boolean(),
    })
        .nullable(),
});
export const dbPortalVertraulichkeit = z.object({
    version: z.string(),
    hash: z.string().length(64),
    titel: z.string(),
    text: z.string(),
    platzhalter: z.boolean(),
});
export const dbPortalVertraulichkeitAkzeptieren = z.object({
    version: z.string().min(1),
    hash: z.string().length(64),
    zugestimmt: z.literal(true),
});
export const DB_PORTAL_CHECKLISTE_ARTEN = ['vertrag', 'rechnung', 'leistungsbeschreibung', 'agb', 'entgeltaenderung'];
export const dbPortalChecklisteArt = z.enum(DB_PORTAL_CHECKLISTE_ARTEN);
export const DB_PORTAL_CHECKLISTE_STATUS = ['fehlt', 'geliefert', 'geprueft', 'nicht_vorhanden'];
export const dbPortalChecklisteStatus = z.enum(DB_PORTAL_CHECKLISTE_STATUS);
export const dbPortalChecklistePunkt = z.object({
    art: dbPortalChecklisteArt,
    jahr: z.number().int(), // 0 = jahresunabhängig
    pflicht: z.boolean(),
    status: dbPortalChecklisteStatus,
    bemerkung: z.string().nullable(),
    geaendertAm: z.string().nullable(),
    /** Rechnungen: Quartale des Jahres, die gelieferte Rechnungen abdecken (Bosch rechnete quartalsweise ab) */
    quartale: z.array(z.number().int().min(1).max(4)).optional(),
});
export const dbPortalFortschritt = z.object({
    pflicht: z.number().int(),
    erledigt: z.number().int(), // geliefert, geprüft oder als nicht vorhanden erklärt
    geliefert: z.number().int(),
    vollstaendig: z.boolean(),
});
export const DB_PORTAL_ZUORDNUNG_ARTEN = ['manuell', 'angabe', 'koordinaten', 'ortsname', 'plz', 'offen'];
export const dbPortalZuordnungArt = z.enum(DB_PORTAL_ZUORDNUNG_ARTEN);
export const dbPortalStandort = z.object({
    id: z.string().uuid(),
    externeId: z.string().nullable(),
    name: z.string(),
    strasse: z.string().nullable(),
    plz: z.string().nullable(),
    ort: z.string().nullable(),
    lat: z.number().nullable(),
    lon: z.number().nullable(),
    gebietSlug: z.string().nullable(),
    zuordnungArt: dbPortalZuordnungArt,
    zuordnungEntfernungKm: z.number().nullable(),
    zuordnungVorschlagSlug: z.string().nullable(),
    prioritaet: z.number().int().min(1).max(3).nullable(),
    bemerkung: z.string().nullable(),
    fortschritt: dbPortalFortschritt,
});
/** Entgeltentwicklung eines Standorts aus den Entgeltänderungen (nach „gültig ab“ sortiert) */
export const dbPortalEntgeltSchritt = z.object({
    dokumentId: z.string().uuid(),
    gueltigAb: z.string().nullable(),
    bisherCent: z.number().int().nullable(),
    neuCent: z.number().int().nullable(),
    erhoehungCent: z.number().int().nullable(),
    prozent: z.number().nullable(),
    stimmig: z.boolean(),
    /** Bezugszeitraum der Beträge (ältere Schreiben: Quartalsentgelt); fehlt = monatlich */
    zeitraum: z.enum(['monat', 'quartal', 'jahr']).optional(),
    /** Anteil Sicherheits-/Zeitsysteme (ohne Leitungsgebühren), falls die Mitteilung die Spalten getrennt ausweist */
    anteilBisherCent: z.number().int().nullable().optional(),
    anteilNeuCent: z.number().int().nullable().optional(),
    anteilProzent: z.number().nullable().optional(),
    /** Lieferung von GQL übernommen (sonst noch in Prüfung) */
    uebernommen: z.boolean(),
});
/** Komponentenpreis eines Standorts (Zeile einer Komponenten-Preistabelle mit Bezug auf das Dokument) */
export const dbPortalKomponentenpreis = z.object({
    dokumentId: z.string().uuid(),
    dateiname: z.string(),
    dokumentArt: dbPortalDokumentArt,
    /** Dokumentdatum (ISO) */
    datum: z.string().nullable(),
    /** Nummer der Tabelle im Dokument (ab 1) */
    tabelle: z.number().int().positive(),
    /** Zeilen der Tabelle ergeben die Tabellensumme (null = keine Summe lesbar) */
    stimmig: z.boolean().nullable(),
    komponente: dbPortalKomponente,
    /** Lieferung von GQL übernommen (sonst noch in Prüfung) */
    uebernommen: z.boolean(),
});
/** Referenz-Kaufpreis einer Komponentenart (assets/db-portal/referenzpreise.json; Entscheidung 08.10.2026: für alle sichtbar) */
export const dbPortalReferenzpreis = z.object({
    art: dbPortalKomponentenArt,
    /** Typbezeichnung des Herstellers oder Norm, z. B. „FMC-120-DKM-H-R“, „DIN 18252“ */
    typ: z.string().max(40).nullable(),
    bezeichnung: z.string().max(200),
    /** Kaufpreis netto in Cent */
    preisCent: z.number().int().nonnegative(),
    bruttoCent: z.number().int().nonnegative().nullable(),
    quelle: z.string().max(300),
    url: z.string().url().nullable(),
    /** Stand bzw. Abrufdatum (ISO) */
    stand: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** funktionsgleiches Gerät eines anderen Herstellers, nicht der in den Unterlagen genannte Typ */
    vergleichsklasse: z.boolean().optional(),
    /** maßgeblicher Eintrag je Art (Excel-Vergleich); sonst der erste */
    primaer: z.boolean().optional(),
});
export const dbPortalStandortDetail = dbPortalStandort.extend({
    checkliste: z.array(dbPortalChecklistePunkt),
    entgeltentwicklung: z.array(dbPortalEntgeltSchritt).optional(),
    komponentenpreise: z.array(dbPortalKomponentenpreis).optional(),
    /** Referenz-Kaufpreise zu den Komponentenarten der Preistabellen (für Vielfaches und Hochrechnung) */
    referenzpreise: z.array(dbPortalReferenzpreis).optional(),
});
export const dbPortalStandortListe = z.object({
    standorte: z.array(dbPortalStandort),
    zusammenfassung: z.object({
        standorte: z.number().int(),
        vollstaendig: z.number().int(),
        ohneGebiet: z.number().int(),
    }),
});
export const dbPortalChecklisteAendern = z.object({
    art: dbPortalChecklisteArt,
    jahr: z.number().int().min(0).max(2100),
    // „geprüft“ setzt nur gql_admin (serverseitig erzwungen)
    status: dbPortalChecklisteStatus,
    bemerkung: z.string().trim().max(500).nullable().optional(),
});
export const dbPortalImportZeile = z.object({
    zeile: z.number().int(),
    name: z.string().nullable(),
    aktion: z.enum(['neu', 'aktualisiert', 'fehler']),
    gebietSlug: z.string().nullable(),
    zuordnungArt: dbPortalZuordnungArt.nullable(),
    fehler: z.array(z.string()),
    hinweise: z.array(z.string()),
});
export const dbPortalImportErgebnis = z.object({
    uebernommen: z.boolean(), // false = nur geprüft (Vorschau)
    zeilen: z.number().int(),
    neu: z.number().int(),
    aktualisiert: z.number().int(),
    fehler: z.number().int(),
    ohneGebiet: z.number().int(),
    details: z.array(dbPortalImportZeile),
});
//# sourceMappingURL=db-portal.js.map